// --- Wheel Settings ---
// DEFAULT values, overridden at runtime via WheelSettingsContext.
// All values are clamped to safe bounds.

// --- BOUNDS (hard limits for settings UI sliders) ---
export const BOUNDS = {
  spinCount:     { min: 3,    max: 20,   step: 1    },  // full rotations
  duration:      { min: 1000,  max: 20000, step: 500  },  // ms (total spin time)
  wheelScale:    { min: 0.8,  max: 2,  step: 0.05 },
  decelerationCoefficient: { min: 1.5, max: 5, step: 0.1 }, // higher = more aggressive slowdown near the end
  winnerDelay:   { min: 0,    max: 2000, step: 100  },  // ms pause before menu
  spinSoundMuteDuration: { min: 0, max: 18000, step: 100 }, // ms of muted pointer sound at spin start
};

// --- DEFAULTS ---
export const DEFAULTS = {
  minSpins: 3,
  maxSpins: 10,
  minDurationMs: 10000,
  maxDurationMs: 20000,
  wheelScale: 1.35,
  decelerationCoefficient: 3,
  edgeBias: false,
  showQuagganEmojis: true,
  clusterNames: true,
  showClusterCountControls: true,
  winnerDelayMs: 0,     // pause before winner menu opens
  spinSoundMuteDurationMs: 2000,
};

// --- HELPERS ---

const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

export const getMaxSpinSoundMuteDurationMs = ({ maxDurationMs }) => {
  return getSpinMotionDurationMs({ durationMs: maxDurationMs });
};

export const sanitizeSettings = (s) => {
  const d = DEFAULTS;
  const b = BOUNDS;
  const minSpins = clamp(s?.minSpins ?? d.minSpins, b.spinCount.min, b.spinCount.max);
  const maxSpins = clamp(s?.maxSpins ?? d.maxSpins, minSpins, b.spinCount.max);
  const fallbackDurationMs = s?.crawlDurationMs ?? d.minDurationMs;
  const minDurationMs = clamp(
    s?.minCrawlDurationMs ?? s?.minDurationMs ?? fallbackDurationMs,
    b.duration.min,
    b.duration.max,
  );
  const maxDurationMs = clamp(
    s?.maxCrawlDurationMs ?? s?.maxDurationMs ?? fallbackDurationMs,
    minDurationMs,
    b.duration.max,
  );
  const wheelScale = clamp(s?.wheelScale ?? d.wheelScale, b.wheelScale.min, b.wheelScale.max);
  const decelerationCoefficient = clamp(
    s?.decelerationCoefficient ?? d.decelerationCoefficient,
    b.decelerationCoefficient.min,
    b.decelerationCoefficient.max,
  );
  const edgeBias = Boolean(s?.edgeBias ?? d.edgeBias);
  const showQuagganEmojis = Boolean(s?.showQuagganEmojis ?? d.showQuagganEmojis);
  const clusterNames = Boolean(s?.clusterNames ?? d.clusterNames);
  const showClusterCountControls = Boolean(
    s?.showClusterCountControls ?? d.showClusterCountControls,
  );
  const winnerDelayMs = clamp(s?.winnerDelayMs ?? d.winnerDelayMs, b.winnerDelay.min, b.winnerDelay.max);
  const maxSpinSoundMuteDurationMs = getMaxSpinSoundMuteDurationMs({ maxDurationMs });
  const spinSoundMuteDurationMs = clamp(
    s?.spinSoundMuteDurationMs ?? d.spinSoundMuteDurationMs,
    b.spinSoundMuteDuration.min,
    maxSpinSoundMuteDurationMs,
  );
  return {
    minSpins,
    maxSpins,
    minDurationMs,
    maxDurationMs,
    wheelScale,
    decelerationCoefficient,
    edgeBias,
    showQuagganEmojis,
    clusterNames,
    showClusterCountControls,
    winnerDelayMs,
    spinSoundMuteDurationMs,
  };
};

export const getNormalizedSpinProgress = (t, decelerationCoefficient = DEFAULTS.decelerationCoefficient) => {
  const normalizedTime = clamp(t, 0, 1);
  const curvePower = Math.max(decelerationCoefficient, 1.0001);
  return 1 - Math.pow(1 - normalizedTime, curvePower);
};

export const getNormalizedSpinVelocity = (t, decelerationCoefficient = DEFAULTS.decelerationCoefficient) => {
  const normalizedTime = clamp(t, 0, 1);
  const curvePower = Math.max(decelerationCoefficient, 1.0001);
  return curvePower * Math.pow(1 - normalizedTime, curvePower - 1);
};

export const getSpinMotionDurationMs = ({ durationMs }) => {
  return durationMs;
};

export const getSpinSpeedRps = ({
  spins,
  durationMs,
  elapsedMs,
  decelerationCoefficient = DEFAULTS.decelerationCoefficient,
}) => {
  const motionDurationMs = getSpinMotionDurationMs({ durationMs });

  if (motionDurationMs <= 0 || elapsedMs < 0 || elapsedMs > motionDurationMs) {
    return 0;
  }

  const totalRotationDegrees = spins * 360;
  const normalizedTime = elapsedMs / motionDurationMs;
  const degreesPerMs =
    (totalRotationDegrees * getNormalizedSpinVelocity(normalizedTime, decelerationCoefficient)) /
    motionDurationMs;

  return (degreesPerMs * 1000) / 360;
};

export const createRandomSpinParams = (settings) => {
  const s = sanitizeSettings(settings);
  const duration = Math.random() * (s.maxDurationMs - s.minDurationMs) + s.minDurationMs;
  const spins = Math.floor(Math.random() * (s.maxSpins - s.minSpins + 1)) + s.minSpins;
  return { spins, duration };
};
